import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import calendar from "dayjs/plugin/calendar";
import {getMarketConfig} from "@atg-shared/market-config";
import isYesterday from "dayjs/plugin/isYesterday";
import {customFormat} from "./customFormatter";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import weekday from "dayjs/plugin/weekday";
import relativeTime from "dayjs/plugin/relativeTime";
import isBetween from "dayjs/plugin/isBetween";
import {isApp} from "@atg-shared/system";

dayjs.extend(calendar);
// Custom format must be imported after LocalizedFormat
// Otherwise the custom format will not be applied
dayjs.extend(LocalizedFormat);
dayjs.extend(customFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isYesterday);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(weekday);
dayjs.extend(relativeTime);
dayjs.extend(isBetween);

/**
 *
 * @param date - The date string, number, Dayjs or Date to parse. It can be a Unix timestamp (As number), a UTC datetime string, or a datetime string in another timezone.
 * @returns A Day.js object representing the parsed date in the market timezone.
 *
 * The function handles three cases:
 * 1. If the date is a Unix timestamp, it converts it to a Day.js object in the market timezone.
 * 2. If the date is a UTC datetime string, Dayjs or Date (containing "Z", "GMT" or "+"), it parses it directly and converts it to the market timezone.
 * 3. If the date does not contain timezone info, it assumes the date is in Europe/Stockholm time and converts it to the market timezone.
 */
export const parseDateTimestamp = (date?: string | number | dayjs.Dayjs | Date) => {
    const marketConfig = getMarketConfig();

    // If date is unix timestamp
    // We dont want to parse UNIX as strings because this can break some date like 20241104
    if (typeof date === "number") {
        const dateStr = date.toString().length;
        if (dateStr === 10) {
            // 10 digits, seconds since the Unix Epoch
            return dayjs.unix(Number(date)).tz(marketConfig.timezone);
        }

        // 13 digits, milliseconds since the Unix Epoch Jan 1 1970 12AM UTC
        return dayjs(Number(date)).tz(marketConfig.timezone);
    }

    const dateTimeString = typeof date !== "string" ? date?.toString() : date;

    // If the dateTimeString can not be parsed to valid Date, we return the Dayjs object directly
    if (!dayjs(dateTimeString).isValid()) {
        return dayjs(dateTimeString).tz(marketConfig.timezone);
    }

    // If the incoming datetime is in UTC or includes timezone information we can parse it directly
    // and return it with the market timezone
    if (
        dateTimeString?.includes("Z") ||
        dateTimeString?.includes("GMT") ||
        dateTimeString?.includes("+")
    ) {
        return dayjs.utc(dateTimeString).tz(marketConfig.timezone);
    }

    // Special case for the app: We need to preserve the original timestamp values to avoid
    // disrupting the app's datetime handling which can cause precision issues,
    // so we create a UTC reference point and use timezone conversions on that
    if (isApp) {
        const inputDate = dayjs(dateTimeString);

        // Create a UTC date with the same date/time values as the input
        const utcReference = dayjs.utc(inputDate.format("YYYY-MM-DDTHH:mm:ss"));

        // Now we can safely use .tz() on UTC dates to get the offsets
        const stockholmTime = utcReference.tz("Europe/Stockholm");
        const marketTime = utcReference.tz(marketConfig.timezone);

        // Calculate the offset difference in minutes
        const offsetDiff = marketTime.utcOffset() - stockholmTime.utcOffset();

        // Apply this offset to our input date
        return inputDate.add(offsetDiff, "minute");
    }

    // If the incoming datetime is not in UTC / has timezone info, we assume it is Europe/Stockholm time and
    // convert it to the market timezone
    return dayjs
        .utc(dayjs.tz(dateTimeString, "Europe/Stockholm"))
        .tz(marketConfig.timezone);
};

/** Parses a date and clock time without an offset in the configured market timezone. */
export const parseMarketDateTime = (dateTime: string) =>
    dayjs.tz(dateTime, getMarketConfig().timezone);
